import { useFetch, useSubmit } from "@hyper-fetch/react";
import { History, Repeat } from "lucide-react";
import { toast } from "sonner";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { surface } from "@/components/layouts/shells/shell-shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DuePayments } from "@/features/payments/modules/due-payments/due-payments";
import { formatDate, money, offerPrice } from "@/features/payments/utils/format";
import { InvoiceAction } from "@/features/payments/modules/invoice-request/invoice-request";
import { WithdrawalAction } from "@/features/payments/modules/withdrawal/withdrawal-action";
import { DownloadButton } from "@/features/shop/modules/download-button/download-button";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const ORDER_STATUS: Record<string, () => string> = {
  paid: () => m.my_payments_order_paid(),
  pending: () => m.my_payments_order_pending(),
  refunded: () => m.my_payments_order_refunded(),
  partially_refunded: () => m.my_payments_order_partially_refunded(),
};

/** How a business says a payment it recorded was made. */
const RECORDED_METHOD: Record<string, () => string> = {
  cash: () => m.my_payments_method_cash(),
  bank_transfer: () => m.my_payments_method_bank_transfer(),
  card_terminal: () => m.my_payments_method_card_terminal(),
  other: () => m.my_payments_method_other(),
};

export type MyPaymentsTab = "history" | "subscriptions";

/** Where a shop parcel is: on its way (with the tracking number) or still being packed. */
function ShippingLine({ order }: { order: { shippedAt: string | null; trackingNumber: string | null } }) {
  return (
    <span className="text-xs text-muted-foreground">
      {order.shippedAt
        ? order.trackingNumber
          ? m.shop_shipped_with_tracking({ date: formatDate(order.shippedAt), number: order.trackingNumber })
          : m.shop_shipped_on({ date: formatDate(order.shippedAt) })
        : m.shop_being_packed()}
    </span>
  );
}

/** A person's payments to their trainers, and their subscriptions with the ways to manage them. */
export function MyPayments({
  tab,
  onTabChange,
  onRedirect = (url) => window.location.assign(url),
}: {
  tab: MyPaymentsTab;
  onTabChange: (tab: MyPaymentsTab) => void;
  onRedirect?: (url: string) => void;
}) {
  const orders = useFetch(sdk.me.payments.orders.$get);
  const subscriptions = useFetch(sdk.me.payments.allSubscriptions.$get);
  const portal = useSubmit(sdk.me.payments.portal.$post);
  const cancel = useSubmit(sdk.me.payments.subscriptions.$clientSubscriptionId.cancel.$post);

  const manage = async (organizationId: string) => {
    const { data, error } = await portal.submit({ queryParams: { organizationId } });
    if (error || !data) {
      toast.error(m.my_payments_action_failed());
      return;
    }
    onRedirect(data.url);
  };

  const end = async (organizationId: string, clientSubscriptionId: string) => {
    const { error } = await cancel.submit({ params: { clientSubscriptionId }, queryParams: { organizationId } });
    if (error) {
      toast.error(m.my_payments_action_failed());
      return;
    }
    client.cache.invalidate(/\/me\/payments/);
    toast.success(m.my_payments_cancelled_toast());
  };

  return (
    <div className="grid gap-6">
      <DuePayments />
      <Tabs value={tab} onValueChange={(value) => onTabChange(value as MyPaymentsTab)}>
        <TabsList>
          <TabsTrigger value="history">
            <History aria-hidden />
            {m.my_payments_tab_history()}
          </TabsTrigger>
          <TabsTrigger value="subscriptions">
            <Repeat aria-hidden />
            {m.my_payments_tab_subscriptions()}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="pt-4">
          {orders.loading && !orders.data ? (
            <Skeleton className="h-40 w-full rounded-xl" />
          ) : !orders.data || orders.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">{m.my_payments_empty()}</p>
          ) : (
            <ul className={cn(surface, "divide-y")}>
              {orders.data.map((order) => (
                <li key={order.orderId ?? order.paymentId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="grid gap-0.5">
                    <span className="font-medium">{order.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {order.businessName} · {formatDate(order.paidAt ?? order.createdAt)}
                      {order.method ? ` · ${(RECORDED_METHOD[order.method] ?? (() => order.method))()}` : ""}
                    </span>
                    {order.needsShipping ? <ShippingLine order={order} /> : null}
                    {order.orderId ? (
                      <div className="pt-1 empty:hidden">
                        <WithdrawalAction
                          withdrawal={order.withdrawal}
                          contract={{ orderId: order.orderId }}
                          title={order.title}
                          seller={order.businessName}
                          price={money(order.amount, order.currency)}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {order.downloadable && order.orderId ? <DownloadButton orderId={order.orderId} /> : null}
                    {order.paymentId && (order.invoiceable || order.invoice !== "none") ? (
                      <InvoiceAction paymentId={order.paymentId} title={order.title} state={order.invoice} />
                    ) : null}
                    <span className="tabular-nums">{money(order.amount, order.currency)}</span>
                    <Badge variant="outline">{(ORDER_STATUS[order.status] ?? (() => order.status))()}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="subscriptions" className="pt-4">
          {subscriptions.loading && !subscriptions.data ? (
            <Skeleton className="h-40 w-full rounded-xl" />
          ) : !subscriptions.data || subscriptions.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">{m.my_payments_subscriptions_empty()}</p>
          ) : (
            <ul className="grid gap-3">
              {subscriptions.data.map((subscription) => {
                const live =
                  subscription.status === "active" ||
                  subscription.status === "trialing" ||
                  subscription.status === "past_due";
                const when = subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : "";
                return (
                  <li key={subscription.clientSubscriptionId} className={cn(surface, "grid gap-3 p-4")}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="grid gap-0.5">
                        <span className="font-medium">{subscription.offerTitle}</span>
                        <span className="text-xs text-muted-foreground">{subscription.businessName}</span>
                      </div>
                      <span className="tabular-nums">
                        {offerPrice({
                          priceAmount: subscription.amount,
                          currency: subscription.currency,
                          interval: subscription.interval,
                          intervalCount: subscription.intervalCount,
                        })}
                      </span>
                    </div>
                    <p
                      className={cn(
                        "text-sm",
                        subscription.status === "past_due" ? "text-destructive" : "text-muted-foreground",
                      )}
                    >
                      {subscription.status === "past_due"
                        ? m.my_payments_subscription_past_due()
                        : !live
                          ? m.my_payments_subscription_canceled()
                          : subscription.cancelAtPeriodEnd
                            ? m.my_payments_subscription_ends({ date: when })
                            : m.my_payments_subscription_renews({ date: when })}
                    </p>
                    <WithdrawalAction
                      withdrawal={subscription.withdrawal}
                      contract={{ clientSubscriptionId: subscription.clientSubscriptionId }}
                      title={subscription.offerTitle}
                      seller={subscription.businessName}
                      price={money(subscription.amount, subscription.currency)}
                    />
                    {live ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void manage(subscription.organizationId)}
                        >
                          {m.my_payments_manage()}
                        </Button>
                        {!subscription.cancelAtPeriodEnd ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => void end(subscription.organizationId, subscription.clientSubscriptionId)}
                          >
                            {m.my_payments_cancel()}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
